import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Common.css';

// Import fallback images
import product1 from '../assets/images/product1.jpg';
import product2 from '../assets/images/product2.jpg';
import product3 from '../assets/images/product3.jpeg';

// Import API service
import { apiService } from '../api/api';

// ✅ Defined OUTSIDE Products so it doesn't remount on every state change
const SidebarContent = ({
  searchTerm,
  setSearchTerm,
  categories = [],
  selectedCategory,
  setSelectedCategory
}) => (
  <div className="sidebar-card">
    {/* Search - auto search, no button */}
    <div className="sidebar-search-section">
      <p className="sidebar-search-label">Search</p>
      <input
        type="text"
        className="sidebar-search-input"
        placeholder="Search products..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>

    {/* Categories */}
    <div className="categories-sidebar">
      <h3 className="sidebar-title">Categories</h3>
      <ul className="categories-list">
        {categories.map((cat) => (
          <li
            key={cat.value}
            className={`category-item ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            <span className="category-name">{cat.name}</span>
            <span className="category-count">({cat.count})</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Products = ({ onProductClick, onNavigate }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(9);

  const handleContactClick = () => {
    if (onNavigate) {
      onNavigate('contact');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fallbackImages = [product1, product2, product3];

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return fallbackImages[0];
    if (imageUrl.includes('default-product.png') || imageUrl.includes('default.png')) return fallbackImages[0];
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      if (imageUrl.includes('api.ecommerce.ashtamtechnologies.com')) return imageUrl.replace('http://', 'https://');
      return imageUrl;
    }
    const base = 'https://uatapi.ecommerce.ashtamtechnologies.com/api/';
    return `${base}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  const extractSalePrice = (product) => {
    const rawValue = product.salePrice ?? product.sale_price;

    if (rawValue === undefined || rawValue === null || rawValue === '') return null;

    const isVisibleDefined = product.isVisiblePrice !== undefined && product.isVisiblePrice !== null;
    if (isVisibleDefined && !Boolean(product.isVisiblePrice)) return null;

    const cleaned = String(rawValue).replace(/[^\d.]/g, '');
    const parsed = parseFloat(cleaned);

    if (isNaN(parsed) || parsed <= 0) return null;

    return parsed;
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAllProductsForDisplay();

        let productData = [];
        if (response && response.data && Array.isArray(response.data)) {
          productData = response.data;
        } else if (Array.isArray(response)) {
          productData = response;
        } else if (response && response.message === 'Successfully' && response.data) {
          productData = response.data;
        }

        if (productData.length > 0) {
          const uniqueCategories = [
            ...new Set(productData.map(p => p.category).filter(c => c && c !== 'N/A'))
          ];
          setDynamicCategories(uniqueCategories);

          const transformedProducts = productData.map((product, index) => {
            const salePrice = extractSalePrice(product);

            return {
              id: index + 1,
              apiProductId: product.id,
              name: product.name?.trim() || product.name?.trim() || `Product ${index + 1}`,
              salePrice,
              rating: 5,
              image: getImageUrl(
                (product.imageUrlList && product.imageUrlList.length > 0)
                  ? product.imageUrlList[0]
                  : product.imageUrl
              ),

              imageUrlList: product.imageUrlList || [],
              category: product.category || 'N/A',
              description: product.description || '',
              originalData: product,
            };
          });

          setProducts(transformedProducts);
          console.log('✅ First product image URL:', transformedProducts[0]?.image);
          console.log('✅ Raw API imageUrl field:', productData[0]?.imageUrl);
          console.log('API product keys:', Object.keys(productData[0]));
          console.log('API image field:', productData[0]?.imageUrl, productData[0]?.image, productData[0]?.image_url);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('❌ Failed to fetch products:', err);
        setError('Failed to load products. Please try again later.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, sortBy]);

  const getCategoriesWithCounts = () => {
    const counts = {};
    products.forEach(p => {
      const cat = p.category || 'N/A';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    const arr = [{ name: 'All Products', value: 'all', count: products.length }];
    dynamicCategories.forEach(cat => {
      arr.push({ name: cat, value: cat, count: counts[cat] || 0 });
    });
    return arr;
  };

  const categories = getCategoriesWithCounts();

  const filteredProducts = products.filter(p => {
    const matchesCat = selectedCategory === 'all' || p.category === selectedCategory;

    const rawSearch = searchTerm.toLowerCase();
    const search = rawSearch.replace(/[$₹,\s]/g, '');

    const matchesSearch = Object.values(p).some((value) => {
      if (value === null || value === undefined) return false;

      if (typeof value === "number") {
        return value.toString().includes(search);
      }

      if (typeof value === "string") {
        return value.toLowerCase().includes(rawSearch);
      }

      return false;
    });

    return matchesCat && matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        if (a.salePrice === null) return 1;
        if (b.salePrice === null) return -1;
        return a.salePrice - b.salePrice;
      case 'price-high':
        if (a.salePrice === null) return 1;
        if (b.salePrice === null) return -1;
        return b.salePrice - a.salePrice;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const getPageNumbers = () => {
    const nums = [];
    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) nums.push(i);
    } else {
      nums.push(1);
      if (currentPage > 3) nums.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) nums.push(i);
      if (currentPage < totalPages - 2) nums.push('...');
      nums.push(totalPages);
    }
    return nums;
  };

  const handlePageChange = (page) => {
    if (page === '...') return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = (product) => {
    // console.log('🔍 ========================================');
    // console.log('🔍 Product clicked in Products.jsx:', product.name);
    // console.log('🔍 salePrice:', product.salePrice);
    // console.log('🔍 Full product data:', product);
    // console.log('🔍 ========================================');

    const productToPass = {
      ...product,
      price: product.salePrice
    };

    if (onProductClick) onProductClick(productToPass);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="products-page">
      {/* Header */}
      <section className="products-header">
        <div className="container">
          <span className="products-label">Products</span>
          <h1 className="products-title">Ready to Equip Your Healthcare Vision?</h1>
          <p className="products-subtitle">
            Tired of searching for the supplies vital to the success of your health care office? Tired of searching for the supplies vital to the success of your health care office?
          </p>
        </div>
      </section>

      {/* Products + Sidebar */}
      <section className="products-section">
        <div className="container">
          <div className="products-layout">

            {/* LEFT: Desktop sidebar — hidden on mobile via CSS */}
            <aside className="sidebar-desktop-col">
              <SidebarContent
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            </aside>

            {/* RIGHT: Products */}
            <div className="products-main-col">
              {/* Sort bar */}
              <div className="sort-bar">
                <p className="showing-results">
                  Showing {sortedProducts.length > 0 ? indexOfFirstProduct + 1 : 0}–
                  {Math.min(indexOfLastProduct, sortedProducts.length)} of {sortedProducts.length} results
                </p>
                <div className="sort-box">
                  <label htmlFor="sortSelect" className="sort-label">Sort by:</label>
                  <select
                    id="sortSelect"
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="default">Default sorting</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A to Z</option>
                  </select>
                </div>
              </div>

              {/* Loading */}
              {loading && (
                <div className="state-message">
                  <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading products...</p>
                </div>
              )}

              {/* Error */}
              {error && !loading && (
                <div className="alert alert-danger">{error}</div>
              )}

              {/* Empty */}
              {!loading && !error && sortedProducts.length === 0 && (
                <div className="state-message">
                  <p className="text-muted">No products found matching your criteria.</p>
                </div>
              )}

              {/* Products grid */}
              {!loading && !error && currentProducts.length > 0 && (
                <div className="products-grid">
                  {currentProducts.map((product) => (
                    <div
                      key={product.id}
                      className="product-grid-item"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="product-card">
                        <div className="product-image-container">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="product-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = fallbackImages[0];
                              console.log('❌ Image failed to load:', product.image);
                            }}
                          />
                        </div>
                        <div className="product-info">
                          <h3 className="product-name">{product.name}</h3>
                          <p className="product-category-badge">{product.category}</p>

                          {product.salePrice === null ? (
                            <div className="product-price-section">
                              <p className="product-price-na">Price: N/A</p>
                              <p className="product-request-price">Request for Price</p>
                            </div>
                          ) : (
                            <p className="product-price">
                              ${product.salePrice.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {!loading && !error && sortedProducts.length > productsPerPage && (
                <div className="pagination-wrap">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>«</button>
                    </li>
                    {getPageNumbers().map((page, i) => (
                      <li key={i} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)} disabled={page === '...'}>{page}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>→</button>
                    </li>
                  </ul>
                </div>
              )}

              {/* MOBILE sidebar — shown below products only on small screens */}
              <aside className="sidebar-mobile-col">
                <SidebarContent
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  categories={categories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                />
              </aside>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <div className="row align-items-center">
              <div className="col-lg-8 mb-3 mb-lg-0">
                <h3 className="cta-title">Contact us today to learn more about our products.</h3>
              </div>
              <div className="col-lg-4 text-lg-end">
                <button className="btn-cta" onClick={handleContactClick}>Contact Us Now</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;